import { SegmentedControl, SegmentedItem } from '@modulor/react';
import clx from 'classnames';
import { useSkeletonTranslation } from 'hooks/useSkeletonTranslation';
import type { FC, FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { getClickStreamEvent, getEventAnalytic, useGetClickStreamCashierContext } from 'services/clickstream';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { clearBonuses, clearSelectedBonus, selectedBonusSelector } from 'store/slices/bonus';
import { flowLockSelector, initialThemeSelector, paymentFlowSelector } from 'store/slices/global';
import { submitRedirectBlankSelector } from 'store/slices/submitForm';
import { activeTabSelector, setActiveTab, tabsEnabledSelector, tabsSelector } from 'store/slices/tabs';
import { getWidgetLocation } from 'utils';
import { useGetSessionId } from 'utils/session';

import styles from './styles.module.scss';

const clickstream = getEventAnalytic();

export const Tabs: FC = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { translate } = useSkeletonTranslation();
  const sessionId = useGetSessionId();
  const isRedirectBlank = useAppSelector(submitRedirectBlankSelector);
  const flowType = useAppSelector(paymentFlowSelector);
  const flowLockState = useAppSelector(flowLockSelector);
  const cashierContext = useGetClickStreamCashierContext(flowType);
  const selectedBonus = useAppSelector(selectedBonusSelector);
  const theme = useAppSelector(initialThemeSelector);

  const tabs = useAppSelector(tabsSelector);
  const { isStatusPage } = getWidgetLocation(location.pathname);
  const activeTab = useAppSelector(activeTabSelector);
  const isEnabled = useAppSelector(tabsEnabledSelector);
  const cryptoTabName = translate('PH.CRYPTO_METHOD_LIST_CAPTION');
  const fiatTabName = translate('PH.CURRENCY.TYPE.FIAT.LABEL');
  const onRampTabName = translate('PH.CURRENCY.TYPE.ONRAMP.LABEL');
  const isCustomClass = theme === 'brand53';

  const TAB_NAMES_MAP = {
    crypto: cryptoTabName,
    fiat: fiatTabName,
    onRamp: onRampTabName,
  };

  const resetWithoutbonusSelection = () => {
    if (selectedBonus?.withoutBonus) {
      dispatch(clearBonuses());
      dispatch(clearSelectedBonus());
    }
  };

  const handleTabClick = (tabValue: string | FormEvent) => {
    resetWithoutbonusSelection();
    if (tabValue !== activeTab) {
      const type = tabValue as string;
      dispatch(setActiveTab(type));
      const event = getClickStreamEvent.pw_currency_type_click({
        sessionId,
        type,
      });
      clickstream.push(event, [cashierContext]);
    }
  };

  if (!isEnabled || isStatusPage || isRedirectBlank) {
    return null;
  }

  return (
    <SegmentedControl
      small
      onChange={handleTabClick}
      className={clx(styles.segmentTabContainer, flowLockState.isLocked && styles.disabled, {
        [styles.custom_brand53]: isCustomClass,
      })}
    >
      {tabs.map((tab) => {
        const isSelected = activeTab === tab;

        return (
          <SegmentedItem key={tab} value={tab} checked={isSelected} dataTestId={`tab-${tab}`}>
            <span>{TAB_NAMES_MAP[tab as keyof typeof TAB_NAMES_MAP] ?? tab}</span>
          </SegmentedItem>
        );
      })}
    </SegmentedControl>
  );
};
